import { renderIdle } from './views/idle';
import { renderActive } from './views/active';
import { renderClaimForm, renderConfirmation } from './views/form';
import { getLang, setLang, saveProfile, clearProfile, type Lang } from './store';
import {
  fetchActiveDrop, submitClaim, uploadPhoto, subscribeDrop,
  type ApiActiveResponse,
} from './api';
import { tr } from './i18n';

// ============================================================================
// App state
// ============================================================================

interface AppState {
  lang: Lang;
  loading: boolean;
  error: string | null;
  drop: ApiActiveResponse | null;
}

const state: AppState = {
  lang: getLang(),
  loading: true,
  error: null,
  drop: null,
};

let unsubscribeWS: (() => void) | null = null;
let selectedPhoto: { file: File; dataUrl: string } | null = null;

// ============================================================================
// Render
// ============================================================================

function mount(html: string) {
  const app = document.getElementById('app')!;
  const fragment = document.createRange().createContextualFragment(html);
  app.replaceChildren(fragment);
}

function render() {
  const lang = state.lang;
  if (state.loading) {
    mount(renderLoading(lang));
  } else if (state.error) {
    mount(renderError(lang, state.error));
  } else if (state.drop?.active) {
    mount(renderActive(lang, state.drop));
    wireActive();
  } else {
    mount(renderIdle(lang));
  }
  wireShell();
}

function renderLoading(_lang: Lang): string {
  return /* html */ `
    <div class="sq-loading">
      <div class="sq-loading-spinner"></div>
    </div>
  `;
}

function renderError(lang: Lang, msg: string): string {
  const retry = lang === 'ro' ? 'Reîncearcă' : 'Retry';
  return /* html */ `
    <div class="sq-loading">
      <p class="sq-loading-error">${escapeHtml(msg)}</p>
      <button class="sq-cta" id="sq-retry">${retry}</button>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// ============================================================================
// Wiring
// ============================================================================

function wireShell() {
  document.querySelectorAll<HTMLButtonElement>('.sq-lang button').forEach((btn) => {
    btn.addEventListener('click', () => {
      const lang = btn.dataset.lang as Lang;
      if (!lang || lang === state.lang) return;
      state.lang = lang;
      setLang(lang);
      render();
    });
  });
  document.getElementById('sq-retry')?.addEventListener('click', () => {
    void refresh();
  });
}

function wireActive() {
  const cta = document.getElementById('sq-claim-cta') as HTMLButtonElement | null;
  if (cta && !cta.disabled) {
    cta.addEventListener('click', () => openClaimForm());
  }
}

// ============================================================================
// Data
// ============================================================================

async function refresh() {
  try {
    state.loading = state.drop === null;
    state.error = null;
    render();
    const data = await fetchActiveDrop();
    state.drop = data;
    state.loading = false;
    state.error = null;
    render();
    setupSubscription();
  } catch (e) {
    state.loading = false;
    state.error = e instanceof Error ? e.message : String(e);
    render();
  }
}

function setupSubscription() {
  if (unsubscribeWS) return;
  const dropId = state.drop?.active?.id;
  if (!dropId) return;
  unsubscribeWS = subscribeDrop(dropId, () => {
    // Any event = re-fetch full state. Cheap and accurate.
    void refetchSilent();
  });
}

async function refetchSilent() {
  try {
    const data = await fetchActiveDrop();
    state.drop = data;
    // Don't re-render if modal is open (would close it). Just update underlying state.
    if (!document.querySelector('.sq-modal')) {
      render();
    }
  } catch { /* ignore — next event or manual refresh */ }
}

// ============================================================================
// Modal + form
// ============================================================================

function ensureModalLayer(): HTMLElement {
  let layer = document.getElementById('sq-modal-layer');
  if (!layer) {
    layer = document.createElement('div');
    layer.id = 'sq-modal-layer';
    document.body.appendChild(layer);
  }
  return layer;
}

function closeModal() {
  const layer = document.getElementById('sq-modal-layer');
  if (layer) layer.replaceChildren();
  document.body.classList.remove('sq-modal-open');
  selectedPhoto = null;
}

function openClaimForm() {
  if (!state.drop?.active || state.drop.active.status !== 'active') {
    void refresh();
    return;
  }
  const layer = ensureModalLayer();
  const fragment = document.createRange().createContextualFragment(renderClaimForm(state.lang));
  layer.replaceChildren(fragment);
  document.body.classList.add('sq-modal-open');
  wireForm();
}

function wireForm() {
  const lang = state.lang;
  const L = tr(lang);

  document.getElementById('sq-modal-close')?.addEventListener('click', closeModal);
  document.querySelector('.sq-modal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeModal();
  });

  document.getElementById('sq-reset-profile')?.addEventListener('click', () => {
    clearProfile();
    closeModal();
    openClaimForm();
  });

  const photoInput = document.getElementById('f-dog-photo') as HTMLInputElement;
  const placeholder = document.getElementById('sq-photo-placeholder')!;
  const preview = document.getElementById('sq-photo-preview')!;
  const img = document.getElementById('sq-photo-img') as HTMLImageElement;
  const changeBtn = document.getElementById('sq-photo-change');

  photoInput?.addEventListener('change', async () => {
    const file = photoInput.files?.[0];
    if (!file) return;
    const dataUrl = await readAsDataUrl(file);
    selectedPhoto = { file, dataUrl };
    img.src = dataUrl;
    placeholder.hidden = true;
    preview.hidden = false;
  });

  changeBtn?.addEventListener('click', () => {
    photoInput.value = '';
    selectedPhoto = null;
    placeholder.hidden = false;
    preview.hidden = true;
  });

  const form = document.getElementById('sq-form') as HTMLFormElement;
  const submitBtn = document.getElementById('sq-form-submit') as HTMLButtonElement;
  const errorBox = document.getElementById('sq-form-error') as HTMLDivElement;

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorBox.hidden = true;
    errorBox.textContent = '';

    const dropId = state.drop?.active?.id;
    if (!dropId || state.drop?.active?.status !== 'active') {
      closeModal();
      await refresh();
      return;
    }

    if (!selectedPhoto) {
      errorBox.textContent = L.err_photo;
      errorBox.hidden = false;
      return;
    }
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const fd = new FormData(form);
    if (fd.get('consent_terms') !== 'on' || fd.get('consent_content_usage') !== 'on') {
      errorBox.textContent = L.err_consent;
      errorBox.hidden = false;
      return;
    }

    const ownerData = {
      dog_name: String(fd.get('dog_name') || '').trim(),
      dog_breed: String(fd.get('dog_breed') || '').trim(),
      dog_age_range: String(fd.get('dog_age_range') || ''),
      dog_size: String(fd.get('dog_size') || ''),
      dog_gender: String(fd.get('dog_gender') || ''),
      dog_city: String(fd.get('dog_city') || '').trim(),
      dog_superpower: String(fd.get('dog_superpower') || '').trim(),
      dog_hobby: String(fd.get('dog_hobby') || '').trim(),
      owner_name: String(fd.get('owner_name') || '').trim(),
      instagram_handle: normalizeHandle(String(fd.get('instagram_handle') || '')),
      email: String(fd.get('email') || '').trim().toLowerCase(),
      phone: String(fd.get('phone') || '').trim(),
    };

    submitBtn.disabled = true;
    submitBtn.textContent = L.btn_submit_loading;

    try {
      // 1. Upload photo
      const photoRes = await uploadPhoto(selectedPhoto.file);

      // 2. Submit claim
      const claimRes = await submitClaim(dropId, {
        ...ownerData,
        dog_photo_key: photoRes.key,
        consent_terms: true,
        consent_content_usage: true,
        consent_newsletter: fd.get('consent_newsletter') === 'on',
        lang,
      });

      if ('ok' in claimRes && claimRes.ok === false) {
        const err = claimRes.error;
        if (err === 'already_claimed') {
          errorBox.textContent = L.err_dup;
        } else if (err === 'drop_not_active') {
          errorBox.textContent = L.err_full;
          setTimeout(() => { closeModal(); void refresh(); }, 1500);
        } else if (err === 'drop_full') {
          errorBox.textContent = L.err_full;
          setTimeout(() => { closeModal(); void refresh(); }, 1500);
        } else {
          errorBox.textContent = `Error: ${err}`;
        }
        errorBox.hidden = false;
        submitBtn.disabled = false;
        submitBtn.textContent = L.btn_submit;
        return;
      }

      saveProfile(ownerData);

      const ok = claimRes as { ok: true; position: number; on_waitlist: boolean };
      showConfirmation(ownerData.dog_name, ok.position, ok.on_waitlist);
      // refresh state in background
      void refetchSilent();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errorBox.textContent = msg;
      errorBox.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = L.btn_submit;
    }
  });
}

function showConfirmation(dogName: string, position: number, onWaitlist: boolean) {
  const layer = ensureModalLayer();
  const fragment = document.createRange().createContextualFragment(
    renderConfirmation(state.lang, dogName, position, onWaitlist),
  );
  layer.replaceChildren(fragment);
  document.getElementById('sq-confirm-close')?.addEventListener('click', () => {
    closeModal();
    void refresh();
  });
}

function normalizeHandle(h: string): string {
  const x = h.trim();
  if (!x) return '';
  return x.startsWith('@') ? x : '@' + x;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// Boot — route by pathname (admin SPA at /admin)
// ============================================================================

if (location.pathname.startsWith('/admin')) {
  void import('./admin/app').then((m) => m.bootAdmin());
} else {
  void refresh();
}
